import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'src/services/api';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { objectToQueryString } from 'src/utils/uri';

const MANAGED_BASE_POLICY_OPTIONS_QUERY_KEY = 'environmentWdacManagedBasePolicyOptions';
const MANAGED_BASE_POLICY_SETTINGS_QUERY_KEY = 'environmentWdacManagedBasePolicySettings';
export const ENVIRONMENT_POLICIES_QUERY_KEY = 'environmentWdacPolicies';
export const MANAGED_BASE_POLICIES_QUERY_KEY = 'environmentWdacManagedBasePolicies';
export const BASE_POLICY_GROUPS_QUERY_KEY = 'environmentWdacBasePolicyGroups';
const POLICY_DETAILS_QUERY_KEY = 'environmentWdacPolicyDetails';
const POLICY_VERSIONS_QUERY_KEY = 'environmentWdacPolicyVersions';
const POLICY_DEVICES_QUERY_KEY = 'environmentWdacPolicyDevices';
const POLICY_TAB_COUNTS_QUERY_KEY = 'environmentWdacPolicyTabCounts';

const getPolicyDetails = ({ customerEnvId, envPolicyId, signal }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/policy/${envPolicyId}`, {
    ...(signal ? { signal } : {}),
  });

const getPolicyTabCounts = ({ customerEnvId, envPolicyId, signal }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/${envPolicyId}/tabCounts`, {
    ...(signal ? { signal } : {}),
  });

const getPolicyDevices = ({ customerEnvId, envPolicyId, hasPolicy, search, page, limit, signal }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/${envPolicyId}/devices`, {
    params: {
      hasPolicy,
      search,
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getPolicyDevicesCsv = ({ customerEnvId, envPolicyId, hasPolicy, search }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/${envPolicyId}/exportDevices`, {
    params: {
      hasPolicy,
      search,
    },
  });

const getPolicyVersionDevices = ({ customerEnvId, envPolicyId, versionId, page, limit, signal }) =>
  api.get(
    `/environmentWdacPolicy/${customerEnvId}/policy/${envPolicyId}/versions/${versionId}/devices`,
    {
      params: {
        page,
        limit,
      },
      ...(signal ? { signal } : {}),
    },
  );

const getPolicyVersions = ({ customerEnvId, envPolicyId, search, page, limit, signal }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/policy/${envPolicyId}/versions`, {
    params: {
      search,
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getManagedBasePolicyOptions = ({ customerEnvId, signal }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/managedBasePolicyOptions`, {
    ...(signal ? { signal } : {}),
  });

const getManagedBasePolicySettings = ({ customerEnvId, envPolicyId, signal }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/managedBasePolicy/${envPolicyId}`, {
    ...(signal ? { signal } : {}),
  });

const getEnvironmentPolicies = ({ customerEnvId, page, limit, query, signal }) =>
  api.get(`/environmentWdacPolicy/${customerEnvId}/policies?${objectToQueryString(query)}`, {
    params: {
      page,
      limit,
    },
    ...(signal ? { signal } : {}),
  });

const getManagedBasePolicies = ({ customerEnvId, page, limit, query, signal }) =>
  api.get(
    `/environmentWdacPolicy/${customerEnvId}/managedBasePolicies?${objectToQueryString(query)}`,
    {
      params: {
        page,
        limit,
      },
      ...(signal ? { signal } : {}),
    },
  );

const getBasePolicyGroups = ({ customerEnvId, page, limit, query, signal }) =>
  api.get(
    `/environmentWdacPolicy/${customerEnvId}/basePolicyGroups?${objectToQueryString(query)}`,
    {
      params: {
        page,
        limit,
      },
      ...(signal ? { signal } : {}),
    },
  );

const postBasePolicy = ({ customerEnvId, payload }) =>
  api.post(`/environmentWdacPolicy/${customerEnvId}/basePolicy`, payload);

const putManagedBasePolicy = ({ customerEnvId, envPolicyId, payload }) =>
  api.put(`/environmentWdacPolicy/${customerEnvId}/managedBasePolicy/${envPolicyId}`, payload);

const postTogglePortalManaged = ({ customerEnvId, envPolicyId }) =>
  api.post(`/environmentWdacPolicy/${customerEnvId}/policy/${envPolicyId}/togglePortalManaged`);

const postManageBasePolicy = ({ customerEnvId, envPolicyId, payload }) =>
  api.post(`/environmentWdacPolicy/${customerEnvId}/policy/${envPolicyId}/manage`, payload);

const postToggleHidePolicy = ({ customerEnvId, envPolicyId }) =>
  api.post(`/environmentWdacPolicy/${customerEnvId}/policy/${envPolicyId}/toggleHide`);

// The base policies a supplemental component can be generated against, which is exactly the base
// policies the portal manages.
export const useManagedBasePolicyOptions = ({ enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [MANAGED_BASE_POLICY_OPTIONS_QUERY_KEY, customerEnvId],
    ({ signal }) => getManagedBasePolicyOptions({ customerEnvId, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

// Everything a base policy's settings decide - whether the portal manages it, whether it is the
// environment default, what it deploys to - is read back by one of these lists or by the form
// itself, so a write to any of them refreshes the lot.
const invalidatePolicyQueries = queryClient => {
  [
    MANAGED_BASE_POLICY_OPTIONS_QUERY_KEY,
    MANAGED_BASE_POLICY_SETTINGS_QUERY_KEY,
    ENVIRONMENT_POLICIES_QUERY_KEY,
    MANAGED_BASE_POLICIES_QUERY_KEY,
    BASE_POLICY_GROUPS_QUERY_KEY,
    POLICY_DETAILS_QUERY_KEY,
    POLICY_VERSIONS_QUERY_KEY,
    POLICY_DEVICES_QUERY_KEY,
    POLICY_TAB_COUNTS_QUERY_KEY,
  ].forEach(queryKey => queryClient.invalidateQueries({ queryKey: [queryKey], exact: false }));
};

// Everything the managed-base-policy form edits, for a base policy the portal already manages or is
// about to: the deployment toggles and the tags it is targeted at.
export const useManagedBasePolicySettings = ({ envPolicyId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [MANAGED_BASE_POLICY_SETTINGS_QUERY_KEY, customerEnvId, envPolicyId],
    ({ signal }) => getManagedBasePolicySettings({ customerEnvId, envPolicyId, signal }),
    {
      enabled: enabled && !!customerEnvId && !!envPolicyId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useCreateBasePolicy = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: payload => postBasePolicy({ customerEnvId, payload }),
    onSuccess: () => invalidatePolicyQueries(queryClient),
  });
};

export const useUpdateManagedBasePolicy = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ envPolicyId, payload }) =>
      putManagedBasePolicy({ customerEnvId, envPolicyId, payload }),
    onSuccess: () => invalidatePolicyQueries(queryClient),
  });
};

export const useTogglePortalManaged = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ envPolicyId }) => postTogglePortalManaged({ customerEnvId, envPolicyId }),
    onSuccess: () => invalidatePolicyQueries(queryClient),
  });
};

// Manages a detected base with its description and whether it is the default, leaving its
// deployment settings as they are.
export const useManageBasePolicy = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ envPolicyId, payload }) =>
      postManageBasePolicy({ customerEnvId, envPolicyId, payload }),
    onSuccess: () => invalidatePolicyQueries(queryClient),
  });
};

export const useTogglePolicyHidden = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ envPolicyId }) => postToggleHidePolicy({ customerEnvId, envPolicyId }),
    onSuccess: () => invalidatePolicyQueries(queryClient),
  });
};

const getNextPageParam = (lastPage, pages) => {
  const loaded = pages.reduce((acc, page) => acc + (page.results?.length || 0), 0);
  return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
};

export const useInfiniteEnvironmentPolicies = ({ apiQuery, limit = 50, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [ENVIRONMENT_POLICIES_QUERY_KEY, apiQuery, customerEnvId, limit],
    ({ pageParam = 1, signal }) =>
      getEnvironmentPolicies({ customerEnvId, page: pageParam, limit, query: apiQuery, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam,
    },
  );
};

// One page of policies for a picker rather than a scrolling list, so it shares the list key and is
// invalidated alongside it.
export const useEnvironmentPoliciesPage = ({ apiQuery, limit = 100, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [ENVIRONMENT_POLICIES_QUERY_KEY, 'page', apiQuery, customerEnvId, limit],
    ({ signal }) =>
      getEnvironmentPolicies({ customerEnvId, page: 1, limit, query: apiQuery, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteBasePolicyGroups = ({ apiQuery, limit = 50, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [BASE_POLICY_GROUPS_QUERY_KEY, apiQuery, customerEnvId, limit],
    ({ pageParam = 1, signal }) =>
      getBasePolicyGroups({ customerEnvId, page: pageParam, limit, query: apiQuery, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam,
    },
  );
};

// The policy with its latest version, and the portal's latest when the portal manages it.
export const usePolicyDetails = ({ envPolicyId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [POLICY_DETAILS_QUERY_KEY, customerEnvId, envPolicyId],
    ({ signal }) => getPolicyDetails({ customerEnvId, envPolicyId, signal }),
    {
      enabled: enabled && !!customerEnvId && !!envPolicyId,
      refetchOnWindowFocus: false,
    },
  );
};

// The tab labels: every version of the policy, and the devices running any of them.
export const usePolicyTabCounts = ({ envPolicyId, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [POLICY_TAB_COUNTS_QUERY_KEY, customerEnvId, envPolicyId],
    ({ signal }) => getPolicyTabCounts({ customerEnvId, envPolicyId, signal }),
    {
      enabled: enabled && !!customerEnvId && !!envPolicyId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfinitePolicyVersions = ({ envPolicyId, search, limit = 20, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [POLICY_VERSIONS_QUERY_KEY, customerEnvId, envPolicyId, search, limit],
    ({ pageParam = 1, signal }) =>
      getPolicyVersions({ customerEnvId, envPolicyId, search, page: pageParam, limit, signal }),
    {
      enabled: enabled && !!customerEnvId && !!envPolicyId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getNextPageParam,
    },
  );
};

// The active devices that have reported one version of the policy, most recent first.
export const useInfinitePolicyVersionDevices = ({
  envPolicyId,
  versionId,
  limit = 48,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [POLICY_DEVICES_QUERY_KEY, 'version', customerEnvId, envPolicyId, versionId, limit],
    ({ pageParam = 1, signal }) =>
      getPolicyVersionDevices({
        customerEnvId,
        envPolicyId,
        versionId,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!envPolicyId && !!versionId,
      refetchOnWindowFocus: false,
      getNextPageParam,
    },
  );
};

// The environment's devices either running some version of the policy or running none of it.
export const useInfinitePolicyDevices = ({
  envPolicyId,
  hasPolicy,
  search,
  limit = 20,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [POLICY_DEVICES_QUERY_KEY, customerEnvId, envPolicyId, hasPolicy, search, limit],
    ({ pageParam = 1, signal }) =>
      getPolicyDevices({
        customerEnvId,
        envPolicyId,
        hasPolicy,
        search,
        page: pageParam,
        limit,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId && !!envPolicyId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getNextPageParam,
    },
  );
};

export const useExportPolicyDevices = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation({
    mutationFn: ({ envPolicyId, hasPolicy, search }) =>
      getPolicyDevicesCsv({ customerEnvId, envPolicyId, hasPolicy, search }),
  });
};

export const useInfiniteManagedBasePolicies = ({ apiQuery, limit = 20, enabled = true }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [MANAGED_BASE_POLICIES_QUERY_KEY, apiQuery, customerEnvId, limit],
    ({ pageParam = 1, signal }) =>
      getManagedBasePolicies({ customerEnvId, page: pageParam, limit, query: apiQuery, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam,
    },
  );
};
